import { useEntity } from '@backstage/plugin-catalog-react';
import {
  decryptAllFilesList,
  fetchData,
  getPrivateKeyByEmail,
  urls,
} from '@internal/backstage-plugin-dh-dock-shared-common';
import {
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  TableContainer,
  TextField,
} from '@material-ui/core';
import LinearProgress from '@mui/material/LinearProgress';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-use';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import AdrList from '../../components/Adr';
import { ADR } from '../../components/Adr/Adr';
import AdrContent from '../../components/Adr/content';
import { ErrorPage } from '../../components/ui/Notifications';
import { RootState } from '../../redux/store';
import {
  getAdrContentBytitle,
  getAdrListByUrl,
} from '../../services/adr.service';

import './Adr.css';

type filterTags = 'title' | 'tag';

const ADRList: React.FC = () => {
  const { email } = useSelector((state: RootState) => state.auth);
  const { entity } = useEntity();
  const location = useLocation();
  const navigate = useNavigate();

  const [adrList, setAdrList] = useState<ADR[]>([]);
  const [filteredList, setFilteredList] = useState<ADR[]>([]);
  const [retrievedContent, setRetrievedContent] = useState('');
  const [selected, setSelected] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [subUrl, setSubUrl] = useState('');
  const [searchParam, setSearchParam] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterBy, setFilterBy] = useState<filterTags>('title');
  const didInitRef = React.useRef(false);
  const [initialPath,setInitialPath] = useState('');  

  const getPath = useCallback(async () => {
    let path=''
    const managedLocation =
      entity?.kind === 'Group'
        ? entity?.metadata?.annotations?.['backstage.io/adr-location']
        : (entity?.metadata?.annotations?.[
            'backstage.io/managed-by-location'
          ] as string);

    let adrLocation = entity?.metadata?.annotations?.[
      'backstage.io/adr-location'
    ] as string;

    if (adrLocation?.replaceAll('url:', '').startsWith('https://')) {
      return adrLocation.replaceAll('url:', '');
    }

    adrLocation = adrLocation.includes('/')
      ? (adrLocation.split('/').pop() as string)
      : adrLocation;

      if(managedLocation?.toLowerCase()?.includes('lhg.ghe.com') && managedLocation?.toLowerCase()?.includes('catalog-info.yaml')){
        path = managedLocation.replace('url:', '');
        path = path.replace('catalog-info.yaml', '')?.concat( entity?.metadata?.annotations?.['backstage.io/adr-location'] as string);
      }
      else{
         path = await fetchData(
      `${urls.getFolderPath}?url=${
        managedLocation?.replace('url:', '').split('?path=')[0]
      }&folder=${adrLocation}`,
    );
      }

    

    return path;
  }, [entity]);

  const fetchAdrList = useCallback(async () => {
    try {
      setIsLoading(true);
      const path = await getPath();
      const privateKey = await getPrivateKeyByEmail(email);
      const list = await getAdrListByUrl(path);

      const decrypted = await decryptAllFilesList(
        list?.data,
        privateKey as CryptoKey,
      );

      if (Array.isArray(decrypted)) {
        const filtered = decrypted.filter(
          x => x?.path && !x.path.toLowerCase().endsWith('.png'),
        ) as any;

        setAdrList(filtered);
        setBaseUrl(path);
      } else {
        setError(list?.message || 'Failed to fetch ADR list');
      }
    } catch (err: any) {
      console.error(err);
      setError('Failed to fetch ADR list');
    } finally {
      setIsLoading(false);
    }
  }, [email, getPath]);

  const fetchContent = useCallback(
    async (id: string) => {
      try {
        const response = await getAdrContentBytitle(`${baseUrl}/${id}`);

        if (response?.data) {
          setRetrievedContent(response.data);
        }
      } catch (err: any) {
        setError(err.message);
      }
    },
    [baseUrl],
  );

  const handleOnClick = useCallback(
    async (id: string) => {
      setSelected(id);
      setSubUrl(id.split('/')[0]);
      setFilteredList([]);
      setSearchParam('');
      setInitialPath('');
      navigate(id);

      await fetchContent(id);
    },
    [navigate, fetchContent],
  );

  useEffect(() => {
    fetchAdrList();
  }, [fetchAdrList]);

  useEffect(() => {
    if (!adrList.length) return;
    if (didInitRef.current) return;

    didInitRef.current = true;

    const path =
      location.pathname?.split('/adr')[1]?.substring(1) ||
      location.pathname?.split('adr/')[1];

    const match = adrList.find(x =>
      x.path?.toLowerCase().includes(path?.toLowerCase() ?? ''),
    );

    const docToLoad =
      match || adrList.find(x => x.path?.toLowerCase().endsWith('.md'));

    if (docToLoad) {
      fetchContent(docToLoad.path!);
      setInitialPath(docToLoad.initialTreeRelativePath!);
      setSelected(docToLoad.path!);
      navigate(docToLoad.path!);
    }
  }, [adrList, location.pathname, fetchContent]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string, list: ADR[], filter: filterTags) => {
        let filtered: ADR[] = [];
        if (filter === 'tag') {
          filtered = list.filter(x =>
            x.tag?.toLowerCase().includes(value.toLowerCase()),
          );
        } else {
          filtered = list.filter(x =>
            x.title?.toLowerCase().includes(value.toLowerCase()),
          );
        }
        setFilteredList(filtered);
      }, 300),
    [],
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    event.preventDefault();
    setSearchParam(value);

    if (!value) {
      setFilteredList([]);
    } else {
      debouncedSearch(value, adrList, filterBy);
    }
  };

  const handleOnFilterSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setFilterBy(event.target.value as filterTags);
  };

  return (
    <>
      <h1>Architecture Decision Records</h1>

      {isLoading && <LinearProgress />}

      {!isLoading && error && (
        <ErrorPage
          message="Sorry, ADR could not be loaded this time."
          type="info"
        />
      )}

      {!isLoading && !error && adrList.length > 0 && (
        <div style={{ display: 'flex', gap: '20px' }}>
          <TableContainer
            component={Paper}
            style={{
              width: '35rem',
              padding: '2rem',
              height: '100vh',
              overflow: 'scroll',
            }}
          >
            <TextField
              variant="standard"
              placeholder="Search"
              value={searchParam}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {searchParam && (
                      <IconButton
                        onClick={() => {
                          setSearchParam('');
                          setFilteredList([]);
                        }}
                        size="small"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                    <FormControl style={{ width: '146px' }}>
                      <RadioGroup
                        style={{ display: 'flex', flexDirection: 'row' }}
                        onChange={handleOnFilterSelect}
                        value={filterBy}
                      >
                        <FormControlLabel
                          value="title"
                          control={<Radio size="small" color="primary" />}
                          label="Title"
                        />
                        <FormControlLabel
                          value="tag"
                          control={<Radio size="small" color="primary" />}
                          label="Tag"
                        />
                      </RadioGroup>
                    </FormControl>
                  </InputAdornment>
                ),
              }}
              style={{ width: '100%' }}
            />
            {filteredList.length > 0 && (
              <ul
                style={{
                  listStyleType: 'none',
                  padding: 0,
                  margin: '8px 0 0 0',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  maxHeight: '100vh',
                  overflowY: 'auto',
                  backgroundColor: 'white',
                  width: '100%',
                }}
              >
                {filteredList.map((item, idx) => (
                  <li
                    key={idx}
                    onClick={() => handleOnClick(item.path!)}
                    style={{
                      padding: '8px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee',
                    }}
                  >
                    {item.title}
                  </li>
                ))}
              </ul>
            )}
            {(adrList.length > 0 ? adrList : adrList)
              .filter(item => item.path?.endsWith('.md'))
              .map(item => {
                if(item?.title) {
                  return (
                    <AdrList
                      key={item.path}
                      status={item.status}
                      name={item.name}
                      title={item.title}
                      date={item.date}
                      selected={selected}
                      path={item.path}
                      initialTreeRelativePath={item.initialTreeRelativePath}
                      onClick={handleOnClick}
                    />
                  );
                }
                return null;
              })}
          </TableContainer>

          <AdrContent
            baseUrl={subUrl ? `${baseUrl}/${subUrl}` : baseUrl}
            innerUrl={initialPath!}
            retrievedContent={retrievedContent}
          />
        </div>
      )}
    </>
  );
};

export default ADRList;
